import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useNavigate, useOutletContext } from "react-router";
import type { LoadFilterOptions } from "@generated/import-openapi.gen";

import type { StreamResource } from "@prequel/import-react";
import { useStream } from "@prequel/import-react";
import {
  Button,
  ButtonStyle,
  checkTabIsActive,
  IdValue,
  MenuItemStyle,
  Modal,
  Spinner,
} from "@prequel/shared-components";

import { useRunLoad } from "@/api/import/streams";
import DetailPageLayout from "@/components/import/DetailPageLayout";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import RunLoadModal from "@/pages/import/StreamsPage/RunLoadModal";
import { useToast } from "@/providers/ToastProvider";

export type StreamContext = {
  stream: StreamResource;
  refreshStream: () => void;
  loadsRefreshKey?: number;
};

export function useStreamContext(): StreamContext {
  return useOutletContext<StreamContext>();
}

const StreamPage = () => {
  const streamID = useRequiredParam("streamID");
  const { addToast } = useToast();
  const { data: stream, error, isLoading, refetch: refreshStream } = useStream(streamID);

  useEffect(() => {
    if (error) {
      addToast({ kind: "ERROR", message: "Failed to load stream." });
    }
  }, [error, addToast]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!stream) {
    return <Navigate to="/import/streams" replace />;
  }

  return <Outlet context={{ stream, refreshStream }} />;
};

export const StreamDetailsPage = () => {
  const navigate = useNavigate();
  const { stream, refreshStream } = useStreamContext();
  const runLoad = useRunLoad();
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [loadsRefreshKey, setLoadsRefreshKey] = useState(0);
  const basePath = `/import/streams/${stream.id}`;

  const submitLoad = (filter?: LoadFilterOptions) =>
    runLoad.mutate(
      { id: stream.id, filter },
      {
        onSuccess: () => {
          setShowFilterModal(false);
          setLoadsRefreshKey((key) => key + 1);
          navigate(`${basePath}/loads`);
        },
      },
    );

  const tabs = [
    <NavLink key="Overview" to={basePath} className={checkTabIsActive} end>
      Overview
    </NavLink>,
    <NavLink key="Loads" to={`${basePath}/loads`} className={checkTabIsActive} end>
      Loads
    </NavLink>,
    <NavLink key="Admin" to={`${basePath}/admin`} className={checkTabIsActive} end>
      Admin
    </NavLink>,
  ];

  return (
    <DetailPageLayout
      title={stream.name}
      onGoBack={() => navigate("/import/streams")}
      subheading={
        <div className="flex items-center gap-2">
          <IdValue id={stream.id} />
        </div>
      }
      tabs={tabs}
      actions={
        <Button
          text="Run Load"
          type={ButtonStyle.TERTIARY}
          disabled={runLoad.isPending}
          onClick={() => submitLoad()}
          menuItems={[
            {
              label: "Run load with filter",
              style: MenuItemStyle.PRIMARY,
              onClick: () => setShowFilterModal(true),
            },
          ]}
        />
      }
    >
      <Modal open={showFilterModal} onClose={() => setShowFilterModal(false)}>
        {showFilterModal && (
          <RunLoadModal
            isSubmitting={runLoad.isPending}
            onCancel={() => setShowFilterModal(false)}
            onSubmit={(filter) => submitLoad(filter)}
          />
        )}
      </Modal>
      <Outlet context={{ stream, refreshStream, loadsRefreshKey }} />
    </DetailPageLayout>
  );
};

export default StreamPage;
